import { useEffect, useState } from "react";
import type { Me } from "../lib/api.js";
import { Boundary } from "../components/Boundary.js";
import Machines from "./Machines.js";
import Scripts from "./Scripts.js";
import Fleet from "./Fleet.js";
import Security from "./Security.js";
import Users from "./Users.js";
import Groups from "./Groups.js";
import AccessControl from "./AccessControl.js";
import AddressBooks from "./AddressBooks.js";
import Audit from "./Audit.js";
import Strategies from "./Strategies.js";
import Relays from "./Relays.js";
import Generator from "./Generator.js";
import Settings from "./Settings.js";
import Account from "./Account.js";

interface NavEntry {
  path: string;
  label: string;
  icon: string;
  perm?: string;
}

const NAV: Array<{ title: string; items: NavEntry[] }> = [
  {
    title: "Operate",
    items: [
      { path: "/machines", label: "Machines", icon: "▣", perm: "device:read" },
      { path: "/scripts", label: "Scripts", icon: "⌬", perm: "script:read" },
      { path: "/fleet", label: "Fleet", icon: "◫", perm: "device:read" },
      { path: "/address-books", label: "Address books", icon: "☰", perm: "ab:read" },
      { path: "/audit", label: "Activity", icon: "◷", perm: "audit:read" },
    ],
  },
  {
    title: "Access",
    items: [
      { path: "/users", label: "People", icon: "⚇", perm: "user:read" },
      { path: "/groups", label: "Groups", icon: "⊞", perm: "group:read" },
      { path: "/access", label: "Permissions", icon: "⛨", perm: "acl:read" },
      { path: "/security", label: "Security", icon: "⚿", perm: "acl:read" },
    ],
  },
  {
    title: "Configure",
    items: [
      { path: "/strategies", label: "Settings profiles", icon: "≡", perm: "strategy:read" },
      { path: "/generator", label: "Installers", icon: "⤓", perm: "client:generate" },
      { path: "/relays", label: "Relays", icon: "⇄", perm: "relay:read" },
      { path: "/settings", label: "Server", icon: "⚙", perm: "settings:read" },
    ],
  },
];

function can(me: Me, perm?: string): boolean {
  if (!perm) return true;
  if (me.permissions.includes("*")) return true;
  if (me.permissions.includes(perm)) return true;
  return me.permissions.includes(perm.split(":")[0] + ":*");
}

export default function Shell({
  me,
  route,
  navigate,
  onSignOut,
}: {
  me: Me;
  route: string;
  navigate: (to: string) => void;
  onSignOut: () => void;
}) {
  const [railOpen, setRailOpen] = useState(false);

  useEffect(() => {
    setRailOpen(false);
  }, [route]);

  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => can(me, i.perm)) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="shell">
      <nav className="rail" data-open={railOpen}>
        <div className="rail__brand">
          <span className="rail__mark">RD</span>
          <div style={{ minWidth: 0 }}>
            <div className="rail__name">RustDesk</div>
            <div className="rail__sub">Self-hosted</div>
          </div>
        </div>

        <div className="rail__nav">
          {groups.map((g) => (
            <div className="rail__group" key={g.title}>
              <div className="rail__grouptitle">{g.title}</div>
              {g.items.map((item) => (
                <button
                  key={item.path}
                  className="navitem"
                  aria-current={route.startsWith(item.path) ? "page" : undefined}
                  onClick={() => navigate(item.path)}
                >
                  <span className="navitem__icon" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="rail__foot">
          <span className="avatar">{(me.display_name || me.name).slice(0, 2).toUpperCase()}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="truncate" style={{ fontSize: 13 }}>{me.display_name || me.name}</div>
            <button
              className="btn btn--ghost btn--sm"
              style={{ padding: 0, height: "auto" }}
              onClick={() => navigate("/account")}
            >
              Your account
            </button>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={onSignOut} title="Sign out">
            Sign out
          </button>
        </div>
      </nav>

      <main className="main">
        {/* A page that throws must not take the rail and every other section
            with it, which is what a blank screen actually is. */}
        <Boundary resetKey={route}>
          <Page route={route} me={me} navigate={navigate} onToggleRail={() => setRailOpen((v) => !v)} />
        </Boundary>
      </main>
    </div>
  );
}

function Page({
  route,
  me,
  navigate,
  onToggleRail,
}: {
  route: string;
  me: Me;
  navigate: (to: string) => void;
  onToggleRail: () => void;
}) {
  const common = { me, navigate, onToggleRail };
  if (route.startsWith("/machines")) return <Machines {...common} />;
  if (route.startsWith("/scripts")) return <Scripts {...common} />;
  if (route.startsWith("/fleet")) return <Fleet {...common} />;
  if (route.startsWith("/users")) return <Users {...common} />;
  if (route.startsWith("/groups")) return <Groups {...common} />;
  if (route.startsWith("/access")) return <AccessControl {...common} />;
  if (route.startsWith("/security")) return <Security {...common} />;
  if (route.startsWith("/address-books")) return <AddressBooks {...common} />;
  if (route.startsWith("/audit")) return <Audit {...common} />;
  if (route.startsWith("/strategies")) return <Strategies {...common} />;
  if (route.startsWith("/relays")) return <Relays {...common} />;
  if (route.startsWith("/generator")) return <Generator {...common} />;
  if (route.startsWith("/settings")) return <Settings {...common} />;
  if (route.startsWith("/account")) return <Account {...common} />;
  return <Machines {...common} />;
}

export interface PageProps {
  me: Me;
  navigate: (to: string) => void;
  onToggleRail: () => void;
}
